// frontend/src/components/dfs/LeagueCtaBanner.tsx
//
// Weekend league funnel shown at the top of a FINISHED weekend contest, just
// under the result hero (bar-wedge GTM: contest player -> league creator,
// pitched at peak post-result engagement). Framed by the user's own result so
// it reads as a reaction to them, not a house ad, and routes into the league
// wizard with the competition they just played pre-selected.
//
// Shows on every platform including iOS (the daily-only posture was only ever
// enforced by hiding this one banner; leagues are already reachable in-app via
// the bottom nav).

import React from 'react';
import { Link } from 'react-router-dom';
import { Trophy } from 'lucide-react';
import { mixpanelTrack } from '@/services/mixpanel';

interface Props {
  contestId: number | null;
  competitionId?: number | null;
  competitionName?: string | null;
  /** Entries the user finished ahead of (totalEntries - rank), when known. */
  managersBeaten?: number | null;
}

const LeagueCtaBanner: React.FC<Props> = ({
  contestId,
  competitionId,
  competitionName,
  managersBeaten,
}) => {
  const beatLine =
    managersBeaten != null && managersBeaten > 0
      ? `You beat ${managersBeaten.toLocaleString()} manager${managersBeaten === 1 ? '' : 's'} this week. `
      : '';

  const createPath = competitionId
    ? `/season-long/create-league?competition=${competitionId}`
    : '/season-long/create-league';

  return (
    <div className="bg-surface-card border border-accent-yellow/30 rounded-lg shadow-xl p-4 md:p-5">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <Trophy size={28} className="text-accent-yellow flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-heading text-lg md:text-xl text-accent-yellow leading-tight">
              It's more fun with friends
            </h3>
            <p className="text-sm text-gray-300 mt-1">
              {beatLine}
              Take this crew season-long{competitionName ? ` in ${competitionName}` : ''}: draft your
              squad, set weekly lineups, swing trades, and go head to head all season.
            </p>
          </div>
        </div>
        <Link
          to={createPath}
          onClick={() =>
            mixpanelTrack('League CTA Clicked', {
              Source: 'contest_final_weekend',
              'Contest ID': contestId,
              'Competition ID': competitionId ?? null,
              'Managers Beaten': managersBeaten ?? null,
            })
          }
          className="bg-accent-yellow hover:bg-yellow-500 active:bg-yellow-500 active:scale-[0.98] text-primary font-bold py-2.5 px-5 rounded-lg transition-colors shadow-sm text-center flex-shrink-0"
        >
          Create a League
        </Link>
      </div>
    </div>
  );
};

export default LeagueCtaBanner;
